    import React, { useEffect, useState } from 'react';
    import { useParams, useNavigate } from 'react-router-dom';
    import {
      ArrowLeft, User, Phone, Car, Hash, Ticket, Calendar,
      IndianRupee, FileText, CheckCircle2, Clock, Save, RefreshCw,
      AlertCircle, MapPin, Wrench, XCircle, MinusCircle,
      RotateCcw, AlertTriangle, X, History, Edit2,
    } from 'lucide-react';
    import { customerAPI, serviceAPI, couponAPI } from '../services/api';
    import { useAuth } from '../context/AuthContext';
    import { format } from 'date-fns';
    import toast from 'react-hot-toast';
    
    const ORDINALS      = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th'];
    const SERVICE_TYPES = [
      'PMS Amount Discount', 'Wheel Alignment', '5 Wheel Balance',
      'Diagnostic Charge', 'Upholstery Cleaning', 'Vehicle Washing Cleaning',
      'Pickup Drop Charges', 'Day Shift MOS', 'Night Shift MOS',
    ];
    
    // ── Helpers ──────────────────────────────────────────────────────────────────
    const InfoRow = ({ icon: Icon, label, value }) => (
      <div className="flex items-start gap-2.5">
        <Icon className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{value || '—'}</div>
        </div>
      </div>
    );
    
    const CouponStatusBadge = ({ status }) => {
      const s = status || 'ACTIVE';
      const cfg = {
        ACTIVE:    'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700',
        CANCELLED: 'bg-red-100    dark:bg-red-900/30    text-red-800    dark:text-red-300    border-red-300    dark:border-red-700',
        INACTIVE:  'bg-amber-100  dark:bg-amber-900/30  text-amber-800  dark:text-amber-300  border-amber-300  dark:border-amber-700',
      };
      return (
        <span className={`px-3 py-1 rounded-full text-xs font-bold border ${cfg[s] || cfg.ACTIVE}`}>
          {s}
        </span>
      );
    };
    
    // ── Service cell ──────────────────────────────────────────────────────────────
    const ServiceCell = ({ service, selected, onToggle, isDeactivated, readOnly }) => {
      const isCancelled = service.status === 'CANCELLED';
      const isUsed      = service.status === 'USED';
      const isDisabled  = isDeactivated || isCancelled || isUsed || readOnly;
    
      let cellClass = 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-brand-300 cursor-pointer';
      if (isCancelled)     cellClass = 'bg-red-50  dark:bg-red-950/40  border-red-300  dark:border-red-800';
      else if (isUsed)     cellClass = 'bg-red-50  dark:bg-red-950/40  border-red-300  dark:border-red-800';
      else if (selected)   cellClass = 'bg-brand-50 dark:bg-brand-900/30 border-brand-400 dark:border-brand-600 shadow-sm';
      else if (readOnly)   cellClass = 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 cursor-default';
    
      return (
        <div
          className={`relative rounded-lg border p-2.5 transition-all duration-150 text-center ${cellClass}`}
          onClick={() => !isDisabled && onToggle(service.name)}
          title={isUsed ? `Claimed: ${service.claimDate ? format(new Date(service.claimDate), 'dd MMM yyyy, hh:mm a') : ''}` : service.name}
        >
          <div className={`text-xs font-medium leading-tight ${(isCancelled || isUsed) ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
            {service.name}
          </div>
          <div className="mt-1.5">
            {isCancelled ? (
              <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded">CANCELLED</span>
            ) : isUsed ? (
              <>
                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded">USED</span>
                {service.claimDate && (
                  <div className="text-[10px] text-red-600 dark:text-red-400 mt-0.5 leading-tight">
                    {format(new Date(service.claimDate), 'dd/MM/yy')}
                  </div>
                )}
              </>
            ) : selected ? (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-brand-700 dark:text-brand-400">
                <CheckCircle2 className="w-3 h-3" /> Selected
              </span>
            ) : (
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">Available</span>
            )}
          </div>
        </div>
      );
    };
    
    // ── Cancel confirmation modal ─────────────────────────────────────────────────
    const CancelModal = ({ mode, onConfirm, onClose, loading }) => {
      const [reason, setReason] = useState('');
      const isCancel = mode === 'cancel';
    
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCancel ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                  <AlertTriangle className={`w-5 h-5 ${isCancel ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white">
                    {isCancel ? 'Cancel Coupon' : 'Mark as Inactive'}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {isCancel
                      ? 'This will permanently cancel the coupon.'
                      : 'This will mark the coupon as inactive.'}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>
    
            <div className="p-6 space-y-4">
              <div className={`p-3 rounded-lg text-sm ${isCancel ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'}`}>
                ⚠ Once {isCancel ? 'cancelled' : 'marked inactive'}, all future service claims will be blocked
                and WhatsApp notifications will be disabled.
              </div>
    
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Reason <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={3}
                  placeholder="Enter reason for this action..."
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                />
              </div>
            </div>
    
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => onConfirm(reason)}
                disabled={loading}
                className={`flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2 ${isCancel ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
              >
                {loading
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : isCancel ? 'Yes, Cancel Coupon' : 'Yes, Mark Inactive'
                }
              </button>
            </div>
          </div>
        </div>
      );
    };
    
    // ── Edit Mobile modal ─────────────────────────────────────────────────────────
    const EditMobileModal = ({ currentMobile, onConfirm, onClose, loading }) => {
      const [mobile, setMobile] = useState(currentMobile || '');
      const [error, setError] = useState('');
    
      const handleSubmit = () => {
        const clean = mobile.trim().replace(/\D/g, '');
        if (clean.length !== 10) {
          setError('Mobile number must be exactly 10 digits.');
          return;
        }
        if (clean === currentMobile) {
          setError('New number is same as current number.');
          return;
        }
        setError('');
        onConfirm(clean);
      };
    
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-brand-100 dark:bg-brand-900/30">
                  <Edit2 className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white">Update Mobile Number</h3>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>
    
            <div className="p-6 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Current Number
                </label>
                <div className="text-sm text-gray-500 dark:text-gray-400">{currentMobile || '—'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  New Mobile Number
                </label>
                <input
                  type="text"
                  value={mobile}
                  onChange={e => setMobile(e.target.value)}
                  maxLength={10}
                  placeholder="10 digit mobile number"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                {error && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>}
              </div>
            </div>
    
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : 'Update Mobile'
                }
              </button>
            </div>
          </div>
        </div>
      );
    };
    
    // ── Main component ────────────────────────────────────────────────────────────
    const CustomerDetail = () => {
      const { id }     = useParams();
      const navigate   = useNavigate();
      const { isAdvisor } = useAuth();
    
      const [customer,    setCustomer]    = useState(null);
      const [serviceData, setServiceData] = useState(null);
      const [auditLog,    setAuditLog]    = useState([]);
      const [selected,    setSelected]    = useState(new Set());
      const [loading,     setLoading]     = useState(true);
      const [saving,      setSaving]      = useState(false);
      const [modal,       setModal]       = useState(null); // 'cancel' | 'inactive' | null
      const [modalLoading, setModalLoading] = useState(false);
      const [editMobileOpen, setEditMobileOpen] = useState(false);
      const [mobileLoading,  setMobileLoading]  = useState(false);
    
      useEffect(() => { loadAll(); }, [id]);
    
      const loadAll = async () => {
        setLoading(true);
        try {
          const res = await customerAPI.getById(id);
          const c   = res.data.data;
          setCustomer(c);
          // Identifiers passed forward are coupon_no + mobile.
          // membership_no is NOT used here anymore — many records have it blank,
          // so using it as a lookup key was silently breaking audit/service loads
          // for exactly those customers.
          await Promise.all([
            loadServices(c),
            loadAuditLog(c.coupon_no, c.mobile),
          ]);
        } catch {
          toast.error('Failed to load customer');
          navigate('/search');
        } finally {
          setLoading(false);
        }
      };
    
      const loadServices = async (customerData) => {
        try {
          // Primary key: coupon_no. mobile sent alongside as a secondary check.
          // NOTE: serviceAPI.getByCoupon does not exist yet in your current
          // api.js — it replaces serviceAPI.getByMembership. You must add it
          // (see explanation below) before this will run.
          const res = await serviceAPI.getByCoupon({
            couponNo: customerData.coupon_no,
            mobile:   customerData.mobile,
          });
          setServiceData(res.data.data);
          setSelected(new Set());
        } catch (err) {
          console.error(err);
          toast.error('Failed to load services');
        }
      };
    
      const loadAuditLog = async (couponNo, mobile) => {
        try {
          // couponAPI.getAudit now takes an object, not a bare membershipNo string.
          const res = await couponAPI.getAudit({ couponNo, mobile });
          setAuditLog(res.data.data || []);
        } catch {
          // Non-critical
        }
      };
    
      const toggleService = (name) => {
        if (isAdvisor) return;
        setSelected(prev => {
          const next = new Set(prev);
          next.has(name) ? next.delete(name) : next.add(name);
          return next;
        });
      };
    
      const handleSave = async () => {
        if (isAdvisor) return;
        if (selected.size === 0) {
          toast.error('Please select at least one service to claim');
          return;
        }
        setSaving(true);
        try {
          const res = await serviceAPI.claimServices({
            couponNo:     customer.coupon_no,
            mobile:       customer.mobile,
            membershipNo: customer.membership_no || null, // display/audit only, not used for lookup
            services:     [...selected],
          });
          const { claimed, alreadyClaimed } = res.data.data;
          if (claimed.length > 0)       toast.success(`${claimed.length} service(s) claimed!`);
          if (alreadyClaimed.length > 0) toast(`${alreadyClaimed.length} already claimed`, { icon: '⚠️' });
          await loadAll();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to save services');
        } finally {
          setSaving(false);
        }
      };
    
      const handleCouponAction = async (reason) => {
        if (isAdvisor) return;
        setModalLoading(true);
        try {
          const payload = {
            couponNo:     customer.coupon_no,
            mobile:       customer.mobile,
            membershipNo: customer.membership_no || null, // display/audit only
            reason,
          };
          if (modal === 'cancel') {
            await couponAPI.cancel(payload);
            toast.success('Coupon cancelled successfully');
          } else {
            await couponAPI.inactive(payload);
            toast.success('Coupon marked as inactive');
          }
          setModal(null);
          await loadAll();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Action failed');
        } finally {
          setModalLoading(false);
        }
      };
    
      const handleReactivate = async () => {
        if (isAdvisor) return;
        if (!window.confirm('Reactivate this coupon?')) return;
        try {
          await couponAPI.reactivate({
            couponNo:     customer.coupon_no,
            mobile:       customer.mobile,
            membershipNo: customer.membership_no || null, // display/audit only
          });
          toast.success('Coupon reactivated');
          await loadAll();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Reactivation failed');
        }
      };
    
      const handleMobileUpdate = async (newMobile) => {
        setMobileLoading(true);
        try {
          // couponNo is now always the lookup key. oldMobile travels along as a
          // verification check on the backend; newMobile is the value being set.
          // The previous version picked membershipNo OR couponNo OR oldMobile
          // as a single identifier depending on what existed — that branching
          // is gone because it no longer matches how this system is supposed
          // to work.
          const payload = {
            couponNo:     customer.coupon_no,
            oldMobile:    customer.mobile,
            mobile: newMobile,
            membershipNo: customer.membership_no || null, // display/audit only
          };
    
          await customerAPI.updateMobile(payload);
          toast.success('Mobile number updated');
          setEditMobileOpen(false);
          await loadAll();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to update mobile');
        } finally {
          setMobileLoading(false);
        }
      };
    
      if (loading) {
        return (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        );
      }
      if (!customer) return null;
    
      const couponStatus  = customer.coupon_status || 'ACTIVE';
      const isDeactivated = ['CANCELLED', 'INACTIVE'].includes(couponStatus);
      const usedCount     = serviceData?.usedServices    ?? 0;
      const availCount    = serviceData?.availableServices ?? 0;
      const total         = serviceData?.totalServices   ?? 0;
      const claimPct      = total ? Math.round((usedCount / total) * 100) : 0;
    
      const serviceMap = {};
      serviceData?.services?.forEach(s => { serviceMap[s.name] = s; });
    
      return (
        <div className="max-w-6xl mx-auto space-y-5">
          {/* Modals */}
          {modal && !isAdvisor && (
            <CancelModal
              mode={modal}
              onConfirm={handleCouponAction}
              onClose={() => setModal(null)}
              loading={modalLoading}
            />
          )}
          {editMobileOpen && !isAdvisor && (
            <EditMobileModal
              currentMobile={customer.mobile}
              onConfirm={handleMobileUpdate}
              onClose={() => setEditMobileOpen(false)}
              loading={mobileLoading}
            />
          )}
    
          {/* Back */}
          <button
            onClick={() => navigate('/search')}
            className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Search
          </button>
    
          {/* Advisor read-only banner */}
          {isAdvisor && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-800 text-blue-700 dark:text-blue-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="font-semibold">View-only access. You cannot edit or claim anything on this page.</span>
            </div>
          )}
    
          {/* Cancelled / Inactive banner */}
          {isDeactivated && (
            <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
              couponStatus === 'CANCELLED'
                ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800 text-red-700 dark:text-red-400'
                : 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-400'
            }`}>
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <div>
                <span className="font-semibold">This coupon is {couponStatus}.</span>
                {customer.cancel_reason && (
                  <span className="ml-2 text-sm">Reason: {customer.cancel_reason}</span>
                )}
                {customer.cancelled_by && (
                  <span className="ml-2 text-sm">
                    By {customer.cancelled_by}
                    {customer.cancelled_at && ` on ${format(new Date(customer.cancelled_at), 'dd MMM yyyy, hh:mm a')}`}
                  </span>
                )}
              </div>
            </div>
          )}
    
          {/* Customer header */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-brand-500 to-brand-700 rounded-2xl flex items-center justify-center text-white text-xl font-bold shadow-lg">
                  {customer.customer_name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">{customer.customer_name}</h1>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-800">
                      {customer.coupon_type}
                    </span>
                    {/* Was customer.membership_no — switched to coupon_no since
                        membership_no is blank for many customers and this badge
                        would just render "#" with nothing after it. coupon_no
                        is now the identifier this system is built around, so
                        it's the one shown prominently here. */}
                    <span className="text-sm text-gray-500 dark:text-gray-400">#{customer.coupon_no}</span>
                    <CouponStatusBadge status={couponStatus} />
                  </div>
                </div>
              </div>
    
              {/* Action buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                {!isAdvisor && (
                  isDeactivated ? (
                    <button
                      onClick={handleReactivate}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Reactivate
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setModal('cancel')}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40 font-semibold rounded-lg transition-colors"
                      >
                        <XCircle className="w-3 h-3" />
                        Cancel Coupon
                      </button>
                      <button
                        onClick={() => setModal('inactive')}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 font-semibold rounded-lg transition-colors"
                      >
                        <MinusCircle className="w-3 h-3" />
                        Mark Inactive
                      </button>
                    </>
                  )
                )}
    
                {/* Stats */}
                <div className="flex items-center gap-3 ml-2">
                  <div className="text-center">
                    <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{availCount}</div>
                    <div className="text-xs text-gray-500">Available</div>
                  </div>
                  <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
                  <div className="text-center">
                    <div className="text-xl font-bold text-red-600 dark:text-red-400">{usedCount}</div>
                    <div className="text-xs text-gray-500">Used</div>
                  </div>
                  <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900 dark:text-white">{total}</div>
                    <div className="text-xs text-gray-500">Total</div>
                  </div>
                </div>
              </div>
            </div>
    
            {/* Progress bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                <span>Service utilization</span>
                <span>{claimPct}%</span>
              </div>
              <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all duration-500"
                  style={{ width: `${claimPct}%` }}
                />
              </div>
            </div>
          </div>
    
          {/* Customer info grid */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
              <User className="w-4 h-4 text-brand-500" />
              Customer Information
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              <InfoRow icon={Hash}        label="Membership No"   value={customer.membership_no} />
              <InfoRow icon={Ticket}      label="Coupon No"       value={customer.coupon_no} />
    
              {/* Mobile with edit button */}
              <div className="flex items-start gap-2.5">
                <Phone className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Mobile</div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{customer.mobile || '—'}</div>
                    {!isAdvisor && (
                      <button
                        onClick={() => setEditMobileOpen(true)}
                        className="text-brand-600 dark:text-brand-400 hover:text-brand-700 flex-shrink-0"
                        title="Edit mobile number"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
    
              <InfoRow icon={Car}         label="Vehicle No"      value={customer.vehicle_no} />
              <InfoRow icon={Car}         label="Model"           value={customer.model} />
              <InfoRow icon={Calendar}    label="Membership Date" value={customer.membership_date} />
              <InfoRow icon={Calendar}    label="Expiry Date"     value={customer.expiry_date} />
              <InfoRow icon={IndianRupee} label="Amount"          value={customer.amount ? `₹${parseFloat(customer.amount).toLocaleString('en-IN')}` : null} />
              <InfoRow icon={FileText}    label="Receipt No"      value={customer.receipt_no} />
              {customer.address      && <InfoRow icon={MapPin}  label="Address"         value={customer.address} />}
              {customer.service_station && <InfoRow icon={MapPin} label="Service Station" value={customer.service_station} />}
            </div>
    
            {/* Cancellation details (only if deactivated) */}
            {isDeactivated && (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 grid grid-cols-2 sm:grid-cols-3 gap-4">
                <InfoRow icon={XCircle}     label="Coupon Status"  value={couponStatus} />
                {customer.cancelled_by   && <InfoRow icon={User}     label="Cancelled By"   value={customer.cancelled_by} />}
                {customer.cancelled_at   && <InfoRow icon={Clock}    label="Cancelled At"   value={format(new Date(customer.cancelled_at), 'dd MMM yyyy, hh:mm a')} />}
                {customer.cancel_reason  && <InfoRow icon={FileText} label="Cancel Reason"  value={customer.cancel_reason} />}
              </div>
            )}
          </div>
    
          {/* Services grid */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-brand-500" />
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  {customer.coupon_type} — Services
                </h2>
                {isDeactivated && (
                  <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-bold rounded-full border border-red-300 dark:border-red-700">
                    {couponStatus}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => loadServices(customer)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Refresh
                </button>
                {!isDeactivated && !isAdvisor && selected.size > 0 && (
                  <span className="text-xs text-brand-600 dark:text-brand-400 font-medium">
                    {selected.size} selected
                  </span>
                )}
                {!isDeactivated && !isAdvisor && (
                  <button
                    onClick={handleSave}
                    disabled={saving || selected.size === 0}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition-colors"
                  >
                    {saving
                      ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <Save className="w-3 h-3" />
                    }
                    Save Services
                  </button>
                )}
              </div>
            </div>
    
            {/* Legend */}
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-4 text-xs">
              {!isDeactivated && !isAdvisor && (
                <>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-emerald-100 dark:bg-emerald-900/40 border border-emerald-400" />
                    <span className="text-gray-500 dark:text-gray-400">Available</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-brand-50 dark:bg-brand-900/30 border border-brand-400" />
                    <span className="text-gray-500 dark:text-gray-400">Selected</span>
                  </div>
                </>
              )}
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-red-100 dark:bg-red-950/40 border border-red-400" />
                <span className="text-gray-500 dark:text-gray-400">{isDeactivated ? 'Cancelled' : 'Used'}</span>
              </div>
            </div>
    
            {/* Table */}
            <div className="p-4 overflow-x-auto">
              <table className="w-full border-collapse" style={{ minWidth: '900px' }}>
                <thead>
                  <tr>
                    <th className="p-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-left w-16">Visit</th>
                    {SERVICE_TYPES.map(type => (
                      <th key={type} className="p-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-center">
                        {type}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ORDINALS.map(ord => (
                    <tr key={ord}>
                      <td className="p-2">
                        <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{ord}</span>
                      </td>
                      {SERVICE_TYPES.map(type => {
                        const svcName = `${ord} ${type}`;
                        const svc = serviceMap[svcName] || { name: svcName, status: isDeactivated ? 'CANCELLED' : 'available', claimDate: null };
                        return (
                          <td key={type} className="p-1">
                            <ServiceCell
                              service={svc}
                              selected={selected.has(svcName)}
                              onToggle={toggleService}
                              isDeactivated={isDeactivated}
                              readOnly={isAdvisor}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
    
            {/* Bottom save bar */}
            {!isDeactivated && !isAdvisor && selected.size > 0 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-brand-50 dark:bg-brand-900/20 rounded-b-xl">
                <div className="flex items-center gap-2 text-sm text-brand-700 dark:text-brand-400">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-medium">{selected.size} service{selected.size !== 1 ? 's' : ''} selected for claiming</span>
                </div>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
                >
                  {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Services
                </button>
              </div>
            )}
          </div>
    
          {/* Audit Log */}
          {auditLog.length > 0 && (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <History className="w-4 h-4 text-brand-500" />
                Coupon Audit History
              </h2>
              <div className="space-y-3">
                {auditLog.map((entry, i) => (
                  <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                    <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                      entry.action === 'CANCELLED'   ? 'bg-red-500'   :
                      entry.action === 'INACTIVE'    ? 'bg-amber-500' :
                      entry.action === 'REACTIVATED' ? 'bg-emerald-500' : 'bg-gray-400'
                    }`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          entry.action === 'CANCELLED'   ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'     :
                          entry.action === 'INACTIVE'    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                          'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                        }`}>{entry.action}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          by {entry.performed_by}
                          {entry.branch_name && ` (${entry.branch_name})`}
                        </span>
                      </div>
                      {entry.reason && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{entry.reason}</p>
                      )}
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {entry.performed_at ? format(new Date(entry.performed_at), 'dd MMM yyyy, hh:mm a') : '—'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    };
    
    export default CustomerDetail;